import Service from '@ember/service';
import { isBlank, isEmpty, isNone } from '@ember/utils';
import ComputedProperty from '@ember/object/computed';
import { computed } from '@ember/object';

export interface Crumb {
  name: string | ComputedProperty<string> | ComputedProperty<unknown>;
  path: string;
  model?: any;
  helpQuery?: string;
}

export default class CrumbsService extends Service {
  crumbs: Crumb[] = [];

  @computed('crumbs.[]')
  get helpQuery() {
    let { crumbs } = this;

    if (isEmpty(crumbs)) {
      return;
    }

    // If the last page contains a custom help query, submit only that
    let customHelpQuery = crumbs[crumbs.length - 1]?.helpQuery;
    if (!isBlank(customHelpQuery)) {
      return customHelpQuery;
    }

    let crumbsNames = crumbs.filter((crumb) => !isBlank(crumb.name)).map((crumb) => String(crumb.name));

    if (crumbsNames.length === 0) {
      return null;
    }

    if (crumbsNames.length === 1) {
      return crumbsNames[0];
    }

    // Returns the name of the first page, to provide context, and the last page, to add specificity.
    // Avoids the middle pages to prevent polluting the search query, which yields few or poor results
    // (For example, "campaigns/:campaign-id/setup/trigger" only yields "Campaigns Trigger")
    return `${crumbsNames[0]} ${crumbsNames[crumbsNames.length - 1]}`;
  }

  append(crumb: Crumb) {
    let { crumbs } = this;

    if (crumbs.findBy('path', crumb.path)) {
      return;
    }

    crumbs.pushObject(crumb);
  }

  /*
    This gets called from a route's deactivate method, which is defined in
    routes/_extensions. If overriding deactivate in a route with crumbs, make
    sure to call _super(), or else crumbs won't disappear when leaving the
    route.
  */
  rollback(crumb: Crumb) {
    let { crumbs } = this;
    let existingCrumb = crumbs.findBy('path', crumb.path);

    if (!isNone(existingCrumb)) {
      crumbs.removeObject(existingCrumb);
    }
  }

  /*
    This replaces a crumb in place. This is useful if you have a computed or
    dynamic crumb name and it needs to change when a model or other data is
    updated through the UI.
  */
  replace(crumb: Crumb) {
    let existingCrumb = this.crumbs.findBy('path', crumb.path);

    if (!existingCrumb) {
      return;
    }

    let index = this.crumbs.indexOf(existingCrumb);

    this.crumbs.replace(index, 1, [crumb]);
  }

  /*
    This gets called from a setTitle, and will set the document title based on the result.
    Create the title by reversing the order and inserting a '|' delimiter between each crumb.
    This will use the current state of the crumbs for the title.
  */
  createTitle() {
    let { crumbs } = this;

    if (isEmpty(crumbs)) {
      return 'Customer.io';
    }

    let crumbNames = crumbs.map((crumb) => crumb.name);
    return `${crumbNames.reverse().join(' | ')} | Customer.io`;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    crumbs: CrumbsService;
  }
}
